import i18n from "src/i18n/config";
import { z } from "zod";

import { FileContentType, FileInfo } from "@dashboard/files/types";

import { ParameterPolicyRule } from "../types";
import { requiredMessage, requiredText } from "../utils";

const apkTypes = [FileContentType.Apk, FileContentType.ApkV2];

const downloadUrl = /^https?:\/\/[^/]/;
const versionCode = /^\d+$/;

const installPackageSchema = z
    .object({
        source: z.enum(["url", "storage"]),
        url: z.string(),
        storageFileId: z.string(),
        filename: z.string(),
        package: requiredText(),
        version: requiredText(),
    })
    .superRefine(({ source, url, storageFileId, version, package: packageName }, context) => {
        if (source === "url" && !downloadUrl.test(url)) {
            const message = url ? i18n.t("modals:addRule.rules.installPackage.invalidUrl") : requiredMessage();
            context.addIssue({ code: "custom", path: ["url"], message });
        }
        if (source === "storage" && !storageFileId) {
            context.addIssue({ code: "custom", path: ["storageFileId"], message: requiredMessage() });
        }
        if (source === "storage" && storageFileId && !packageName) {
            context.addIssue({
                code: "custom",
                path: ["storageFileId"],
                message: i18n.t("modals:addRule.rules.installPackage.fileNotAnalyzed"),
            });
        }
        if (version && !versionCode.test(version)) {
            context.addIssue({
                code: "custom",
                path: ["version"],
                message: i18n.t("modals:addRule.rules.installPackage.invalidVersion"),
            });
        }
    });

const asText = (value: unknown) => (typeof value === "string" || typeof value === "number" ? String(value) : "");

function fromApk(file: FileInfo) {
    const manifest = file.metadata?.file?.apk?.manifest;
    return { package: manifest?.package ?? "", version: manifest?.versioncode ?? "", filename: file.filename };
}

function apkOptionLabel(file: FileInfo) {
    const manifest = file.metadata?.file?.apk?.manifest;
    if (!manifest?.package) {
        return file.filename;
    }
    return manifest.versionname ? `${manifest.package} (${manifest.versionname})` : manifest.package;
}

export const installPackage: ParameterPolicyRule<z.infer<typeof installPackageSchema>> = {
    id: "installPackage",
    title: () => i18n.t("modals:addRule.rules.installPackage.title"),
    description: () => i18n.t("modals:addRule.rules.installPackage.description"),
    details: () => i18n.t("modals:addRule.rules.installPackage.details"),
    summary: (display) => i18n.t("modals:addRule.rules.installPackage.summary", display),
    categories: ["apps"],
    fields: [
        {
            type: "select",
            name: "source",
            label: () => i18n.t("common:label.source"),
            options: [
                { value: "url", label: () => i18n.t("modals:addRule.rules.installPackage.options.url") },
                { value: "storage", label: () => i18n.t("modals:addRule.rules.installPackage.options.storage") },
            ],
            clears: ["package", "version"],
        },
        {
            type: "text",
            name: "url",
            label: () => i18n.t("modals:addRule.rules.installPackage.url"),
            dependsOn: { name: "source", value: "url" },
        },
        {
            type: "file",
            name: "storageFileId",
            label: () => i18n.t("modals:addRule.rules.installPackage.storageFileId"),
            contentTypes: apkTypes,
            extensions: ["apk"],
            noFilesLabel: () => i18n.t("modals:addRule.rules.installPackage.noFilesAvailable"),
            derives: ["package", "version", "filename"],
            derive: fromApk,
            optionLabel: apkOptionLabel,
            dependsOn: { name: "source", value: "storage" },
        },
        {
            type: "text",
            name: "filename",
            label: () => i18n.t("modals:addRule.rules.installPackage.filename"),
            readOnlyWhen: { name: "source", value: "storage" },
            dependsOn: { name: "source", value: "storage" },
        },
        {
            type: "text",
            name: "package",
            label: () => i18n.t("common:label.packageName"),
            dependsOn: { name: "source", value: "url" },
        },
        {
            type: "text",
            name: "version",
            label: () => i18n.t("modals:addRule.rules.installPackage.version"),
            readOnlyWhen: { name: "source", value: "storage" },
        },
    ],
    defaultValues: { source: "storage", url: "", storageFileId: "", filename: "", package: "", version: "" },
    schema: installPackageSchema,
    command: { command: "installPackage" },
    parameterKeys: ["source", "url", "storageFileId", "package", "version"],
    ignoredKeys: ["file"],
    flags: ["allowDuplicates"],
    toParameters: (values) => ({
        source: values.source,
        ...(values.source === "url" ? { url: values.url } : { storageFileId: values.storageFileId }),
        package: values.package,
        version: values.version,
    }),
    fromParameters: (command) => {
        if (command.source !== undefined && command.source !== "url" && command.source !== "storage") {
            return null;
        }

        return {
            source: command.source === "storage" ? "storage" : "url",
            url: asText(command.url),
            storageFileId: asText(command.storageFileId ?? command.file),
            filename: "",
            package: asText(command.package),
            version: asText(command.version),
        };
    },
};
